import { useInitUserQuery } from "graphql/user";
import { useChatwoot } from "providers/ChatwootProvider";
import { useEffect, useMemo } from "react";
import { GetUserQuery } from "types";

type ChatwootSyncUser = GetUserQuery["user"];
type Company = { id: string; name: string };

const dedupeCompanies = (companies: Company[]): Company[] => {
  const seen = new Set<string>();
  return companies.filter((company) => {
    if (seen.has(company.id)) return false;
    seen.add(company.id);
    return true;
  });
};

const formatCompanies = (companies: Company[]): string =>
  companies.map((company) => `${company.name} (${company.id})`).join(", ");

const buildCustomAttributes = (
  user: ChatwootSyncUser,
): Record<string, string> => {
  const attributes: Record<string, string> = {
    user_id: user.id,
    user_email: user.email,
    user_roles: [
      ...new Set(user.userRoles.map((userRole) => userRole.role.name)),
    ].join(", "),
  };

  const employmentCompanies = dedupeCompanies(
    user.employments.map((employment) => employment.company),
  );
  if (employmentCompanies.length > 0) {
    attributes.employment_companies = formatCompanies(employmentCompanies);
  }

  const adminCompanies = dedupeCompanies(
    user.userRoles.reduce<Company[]>((companies, userRole) => {
      if (userRole.company && !userRole.role.isPlatform) {
        companies.push(userRole.company);
      }
      return companies;
    }, []),
  );
  if (adminCompanies.length > 0) {
    attributes.admin_companies = formatCompanies(adminCompanies);
  }

  return attributes;
};

export const useSyncChatwootUser = (): void => {
  const { data } = useInitUserQuery();
  const { identify, setCustomAttributes, ready } = useChatwoot();

  const user = data?.user;
  const userId = user?.id;
  const userHash = user?.idHash;
  const name = user?.profile?.name;
  const email = user?.email;

  const customAttributes = useMemo(
    () => (user ? buildCustomAttributes(user) : {}),
    [user],
  );

  useEffect(() => {
    if (!ready || !userId) return;

    if (userHash) {
      identify(userId, {
        identifier_hash: userHash,
        ...(name ? { name } : {}),
        ...(email ? { email } : {}),
      });
    }

    if (Object.keys(customAttributes).length > 0) {
      setCustomAttributes(customAttributes);
    }
  }, [
    ready,
    name,
    email,
    userId,
    userHash,
    customAttributes,
    identify,
    setCustomAttributes,
  ]);
};
